'use client';

import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { useGetWlasConfig } from '@/queris/useGetWlasConfig';
import { useUpdateWlasConfig } from '@/queris/useUpdateWlasConfig';
import { useGetWlasFilterOptions } from '@/queris/useGetWlasFilterOptions';
import { showToast } from '@/utils/showToast';
import {
  WlasFilters,
  WlasValues,
  buildAttrs,
  defaultFilters,
  defaultValues,
  isDirtyCheck,
  isDirtyFiltersCheck,
} from './utils';
import { ConfigLayout } from './styled';
import { WlasParamsCard } from './components/WlasParamsCard';
import { WlasQuickstartCard } from './components/WlasQuickstartCard';
import { WlasDataFiltersCard } from './components/WlasDataFiltersCard';
import { WlasFaqCard } from './components/WlasFaqCard';
import { WlasWidgetPreview, WlasWidgetPreviewHandle } from './components/WlasWidgetPreview';

const MemoQuickstartCard = memo(WlasQuickstartCard);
const MemoWidgetPreview = memo(WlasWidgetPreview);
const MemoFaqCard = memo(WlasFaqCard);

export const WlasConfigTab: FC = () => {
  const t = useTranslations('wlasWidget');

  const { data } = useGetWlasConfig();
  const { mutate: saveConfig, isPending: isSaving } = useUpdateWlasConfig();
  const { data: filterOptions } = useGetWlasFilterOptions();

  const [savedParams, setSavedParams] = useState<WlasValues>(defaultValues);
  const [localParams, setLocalParams] = useState<WlasValues>(defaultValues);
  const [previewParams, setPreviewParams] = useState<WlasValues>(defaultValues);
  const [savedFilters, setSavedFilters] = useState<WlasFilters>(defaultFilters());
  const [localFilters, setLocalFilters] = useState<WlasFilters>(defaultFilters());
  const [memberUniqueCode, setMemberUniqueCode] = useState<string>('wlas');
  const [previewAttrs, setPreviewAttrs] = useState<Record<string, string>>({});

  const previewRef = useRef<WlasWidgetPreviewHandle>(null);

  const localParamsRef = useRef(localParams);
  localParamsRef.current = localParams;
  const localFiltersRef = useRef(localFilters);
  localFiltersRef.current = localFilters;
  const memberUniqueCodeRef = useRef(memberUniqueCode);
  memberUniqueCodeRef.current = memberUniqueCode;

  useEffect(() => {
    if (!data) return;
    setMemberUniqueCode(data.memberUniqueCode);
    setSavedParams(data.params);
    setLocalParams(data.params);
    setPreviewParams(data.params);
    setSavedFilters(data.filters);
    setLocalFilters(data.filters);
    setPreviewAttrs(buildAttrs(data.memberUniqueCode, data.params, true));
  }, [data]);

  const isParamsDirty = useMemo(
    () => isDirtyCheck(savedParams, localParams),
    [savedParams, localParams],
  );

  const isPreviewDirty = useMemo(
    () => isDirtyCheck(previewParams, localParams),
    [previewParams, localParams],
  );

  const isFiltersDirty = useMemo(
    () => isDirtyFiltersCheck(savedFilters, localFilters),
    [savedFilters, localFilters],
  );

  const handleParamChange = useCallback((id: string, value: string | boolean) => {
    setLocalParams((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleFilterChange = useCallback(
    <K extends keyof WlasFilters>(key: K, value: WlasFilters[K]) => {
      setLocalFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handlePreview = useCallback(() => {
    const params = localParamsRef.current;
    setPreviewAttrs(buildAttrs(memberUniqueCodeRef.current, params, true));
    setPreviewParams(params);
    previewRef.current?.scrollIntoView();
  }, []);

  const handleSaveParams = useCallback(() => {
    const params = localParamsRef.current;
    const filters = localFiltersRef.current;
    saveConfig(
      { params, filters },
      {
        onSuccess: () => {
          setSavedParams(params);
          setSavedFilters(filters);
          setPreviewParams(params);
          showToast.success(t('toast.saveSuccess'));
          setPreviewAttrs(buildAttrs(memberUniqueCodeRef.current, params, true));
          previewRef.current?.scrollIntoView();
        },
        onError: () => {
          toast.error(t('toast.saveError'));
        },
      },
    );
  }, [saveConfig, t]);

  const handleSaveFilters = useCallback(() => {
    const params = localParamsRef.current;
    const filters = localFiltersRef.current;
    saveConfig(
      { params, filters },
      {
        onSuccess: () => {
          setSavedParams(params);
          setSavedFilters(filters);
          showToast.success(t('toast.saveSuccess'));
        },
        onError: () => {
          toast.error(t('toast.saveError'));
        },
      },
    );
  }, [saveConfig, t]);

  const handleResetParams = useCallback(() => {
    setLocalParams(savedParams);
  }, [savedParams]);

  const handleResetFilters = useCallback(() => {
    setLocalFilters(savedFilters);
  }, [savedFilters]);

  return (
    <ConfigLayout>
      <MemoQuickstartCard memberUniqueCode={memberUniqueCode} />

      <WlasDataFiltersCard
        filters={localFilters}
        filterOptions={filterOptions}
        onChange={handleFilterChange}
        isDirty={isFiltersDirty}
        isSaving={isSaving}
        onSave={handleSaveFilters}
        onResetDefaults={handleResetFilters}
      />

      <WlasParamsCard
        values={localParams}
        onChange={handleParamChange}
        isDirty={isParamsDirty}
        isPreviewDirty={isPreviewDirty}
        isSaving={isSaving}
        onSave={handleSaveParams}
        onPreview={handlePreview}
        onReset={handleResetParams}
      />

      <MemoWidgetPreview ref={previewRef} attrs={previewAttrs} />

      <MemoFaqCard />
    </ConfigLayout>
  );
};
